import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Loader2, RefreshCw, Calendar, TrendingUp } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { callMetricsService } from "../../services/callMetricsService";
import { MetricStatistics } from "../../types/metrics";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import DashboardLayout from "../dashboard/layout/DashboardLayout";

interface OverallLatencyStats {
  organization_id: string;
  total_calls_analyzed: number;
  date_range: {
    start: string;
    end: string;
    days: number;
  };
  stt: MetricStatistics;
  llm: {
    ttft: MetricStatistics;
    duration: MetricStatistics;
    tokens_per_second: MetricStatistics;
    cache_hit_ratio: number;
  };
  tts: {
    ttfb: MetricStatistics;
    duration: MetricStatistics;
  };
  eou: {
    transcription_delay: MetricStatistics;
    end_of_utterance_delay: MetricStatistics;
  };
  overall_response_time: MetricStatistics;
  metric_counts: {
    stt: number;
    llm_ttft: number;
    tts_ttfb: number;
    eou: number;
    overall: number;
  };
}

export const OverallLatencyDashboard: React.FC = () => {
  const [stats, setStats] = useState<OverallLatencyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [limit, setLimit] = useState(100);
  const [days, setDays] = useState(30);

  const fetchOverallLatencyStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get API key from environment
      const apiKey = import.meta.env.VITE_API_KEY || "";

      if (!apiKey) {
        throw new Error("API key not configured");
      }

      const response = await callMetricsService.getOverallLatencyStatistics(
        apiKey,
        limit,
        days
      );

      setStats(response);
    } catch (error) {
      console.error("Error fetching overall latency statistics:", error);
      setError(error instanceof Error ? error.message : "Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOverallLatencyStats();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchOverallLatencyStats();
  }, [limit, days]);

  const formatValue = (value: number, isTokenMetric: boolean = false) => {
    if (isTokenMetric) {
      return value.toFixed(2);
    }
    return callMetricsService.formatDuration(value);
  };

  if (loading && !refreshing) {
    return (
      <DashboardLayout activeItem="Latency Analytics">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading overall latency statistics...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout activeItem="Latency Analytics">
        <div className="container mx-auto p-6">
          <Alert variant="destructive">
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Retry"
                  )}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  if (!stats || stats.total_calls_analyzed === 0) {
    return (
      <DashboardLayout activeItem="Latency Analytics">
        <div className="container mx-auto p-6">
          <Card className="p-8">
            <div className="text-center">
              <p className="text-lg text-muted-foreground mb-4">
                No latency data available.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Make some calls to see latency statistics here.
              </p>
              <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
                {refreshing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const statisticsData = [
    {
      metric: "STT Latency (Transcription Delay)",
      min: stats.stt.min,
      max: stats.stt.max,
      avg: stats.stt.avg,
      median: stats.stt.median,
      p50: stats.stt.p50,
      p75: stats.stt.p75,
      p95: stats.stt.p95,
      p99: stats.stt.p99,
      stddev: stats.stt.stddev,
      count: stats.stt.count,
    },
    ...(stats.eou?.end_of_utterance_delay && stats.eou.end_of_utterance_delay.count > 0 ? [{
      metric: "End of Utterance Detection Delay",
      min: stats.eou.end_of_utterance_delay.min,
      max: stats.eou.end_of_utterance_delay.max,
      avg: stats.eou.end_of_utterance_delay.avg,
      median: stats.eou.end_of_utterance_delay.median,
      p50: stats.eou.end_of_utterance_delay.p50,
      p75: stats.eou.end_of_utterance_delay.p75,
      p95: stats.eou.end_of_utterance_delay.p95,
      p99: stats.eou.end_of_utterance_delay.p99,
      stddev: stats.eou.end_of_utterance_delay.stddev,
      count: stats.eou.end_of_utterance_delay.count,
    }] : []),
    {
      metric: "LLM TTFT (Time to First Token)",
      min: stats.llm.ttft.min,
      max: stats.llm.ttft.max,
      avg: stats.llm.ttft.avg,
      median: stats.llm.ttft.median,
      p50: stats.llm.ttft.p50,
      p75: stats.llm.ttft.p75,
      p95: stats.llm.ttft.p95,
      p99: stats.llm.ttft.p99,
      stddev: stats.llm.ttft.stddev,
      count: stats.llm.ttft.count,
    },
    {
      metric: "LLM Total Duration",
      min: stats.llm.duration.min,
      max: stats.llm.duration.max,
      avg: stats.llm.duration.avg,
      median: stats.llm.duration.median,
      p50: stats.llm.duration.p50,
      p75: stats.llm.duration.p75,
      p95: stats.llm.duration.p95,
      p99: stats.llm.duration.p99,
      stddev: stats.llm.duration.stddev,
      count: stats.llm.duration.count,
    },
    {
      metric: "LLM Tokens/Second",
      min: stats.llm.tokens_per_second.min,
      max: stats.llm.tokens_per_second.max,
      avg: stats.llm.tokens_per_second.avg,
      median: stats.llm.tokens_per_second.median,
      p50: stats.llm.tokens_per_second.p50,
      p75: stats.llm.tokens_per_second.p75,
      p95: stats.llm.tokens_per_second.p95,
      p99: stats.llm.tokens_per_second.p99,
      stddev: stats.llm.tokens_per_second.stddev,
      count: stats.llm.tokens_per_second.count,
      isTokenMetric: true,
    },
    {
      metric: "TTS TTFB (Time to First Byte)",
      min: stats.tts.ttfb.min,
      max: stats.tts.ttfb.max,
      avg: stats.tts.ttfb.avg,
      median: stats.tts.ttfb.median,
      p50: stats.tts.ttfb.p50,
      p75: stats.tts.ttfb.p75,
      p95: stats.tts.ttfb.p95,
      p99: stats.tts.ttfb.p99,
      stddev: stats.tts.ttfb.stddev,
      count: stats.tts.ttfb.count,
    },
    {
      metric: "TTS Total Duration",
      min: stats.tts.duration.min,
      max: stats.tts.duration.max,
      avg: stats.tts.duration.avg,
      median: stats.tts.duration.median,
      p50: stats.tts.duration.p50,
      p75: stats.tts.duration.p75,
      p95: stats.tts.duration.p95,
      p99: stats.tts.duration.p99,
      stddev: stats.tts.duration.stddev,
      count: stats.tts.duration.count,
    },
    {
      metric: "Overall Response Time (EOU + TTFT + TTFB)",
      min: stats.overall_response_time.min,
      max: stats.overall_response_time.max,
      avg: stats.overall_response_time.avg,
      median: stats.overall_response_time.median,
      p50: stats.overall_response_time.p50,
      p75: stats.overall_response_time.p75,
      p95: stats.overall_response_time.p95,
      p99: stats.overall_response_time.p99,
      stddev: stats.overall_response_time.stddev,
      count: stats.overall_response_time.count,
    },
  ];

  return (
    <DashboardLayout activeItem="Latency Analytics">
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Overall Latency Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Aggregate performance metrics across {stats.total_calls_analyzed} calls
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          size="sm"
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
          <CardDescription>Adjust the analysis parameters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Days to Look Back</label>
              <Input
                type="number"
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value) || 30)}
                min={1}
                max={365}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Analyzing calls from the last {days} days
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Max Calls to Analyze</label>
              <Input
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
                min={10}
                max={1000}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of recent calls to include
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Calls Analyzed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_calls_analyzed}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Last {stats.date_range.days} days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Avg Response Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatValue(stats.overall_response_time.avg)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              EOU + TTFT + TTFB
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">P95 Response Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatValue(stats.overall_response_time.p95)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              95th percentile
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">LLM Cache Hit Ratio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.llm.cache_hit_ratio.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Tokens from cache
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Statistics Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Statistics</CardTitle>
          <CardDescription>
            Statistical breakdown of all latency metrics across {stats.total_calls_analyzed} calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[250px]">Metric</TableHead>
                  <TableHead className="text-right">Min</TableHead>
                  <TableHead className="text-right">Max</TableHead>
                  <TableHead className="text-right">Avg</TableHead>
                  <TableHead className="text-right">Median</TableHead>
                  <TableHead className="text-right">P50</TableHead>
                  <TableHead className="text-right">P75</TableHead>
                  <TableHead className="text-right">P95</TableHead>
                  <TableHead className="text-right">P99</TableHead>
                  <TableHead className="text-right">Std Dev</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statisticsData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{row.metric}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatValue(row.min, row.isTokenMetric)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatValue(row.max, row.isTokenMetric)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">
                      {formatValue(row.avg, row.isTokenMetric)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatValue(row.median, row.isTokenMetric)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <Badge variant="outline">
                        {formatValue(row.p50, row.isTokenMetric)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <Badge variant="outline">
                        {formatValue(row.p75, row.isTokenMetric)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <Badge variant="secondary">
                        {formatValue(row.p95, row.isTokenMetric)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <Badge variant="destructive">
                        {formatValue(row.p99, row.isTokenMetric)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-muted-foreground">
                      {formatValue(row.stddev, row.isTokenMetric)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {row.count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
};
